import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import Button from '../components/ui/Button';
import { formatIdrFromUsd } from '../utils/currency';

const OrderComplete = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { order } = location.state || {};
  
  // Use the order number from the server
  const orderNumber = order?.orderNumber || 'Unknown';
  
  // Format date
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  
  // If no order data is available, show an error
  if (!order) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-16 text-center">
          <h1 className="font-serif text-3xl font-bold text-primary mb-6">Order Information Not Found</h1>
          <p className="text-light mb-8">We couldn't find your order information. Please contact customer support.</p>
          <Button onClick={() => navigate('/')} variant="primary">
            Return to Home
          </Button>
        </div>
      </Layout>
    );
  }
  
  return (
    <Layout>
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <div className="w-20 h-20 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="font-serif text-4xl font-bold text-primary mb-4">
              Thank You for Your Order!
            </h1>
            <p className="text-light text-lg max-w-xl mx-auto">
              Your order has been received and is now being processed. We'll send you a confirmation email shortly.
            </p>
          </div>
          
          <div className="bg-secondary border border-gray-800 rounded-lg overflow-hidden mb-8">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="font-serif text-xl font-bold text-primary">
                  Order Summary
                </h2>
                <span className="bg-primary/20 text-primary px-3 py-1 rounded-full text-sm font-medium">
                  {order.status === 'paid' ? 'Payment Successful' : 
                   order.status === 'test_payment' ? 'Test Payment' : 'Processing'}
                </span>
              </div>
              
              <div className="space-y-4 mb-6">
                <div className="flex justify-between">
                  <span className="text-gray-400">Order Number:</span>
                  <span className="text-light font-medium">{orderNumber}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Order Date:</span>
                  <span className="text-light">{formatDate(order.orderDate)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Payment Method:</span>
                  <span className="text-light">
                    {order.paymentMethod === 'credit_card' && 'Credit/Debit Card'}
                    {order.paymentMethod === 'test_payment' && 'Test Payment'}
                  </span>
                </div>
              </div>
              
              <div className="border-t border-gray-800 pt-6 mb-6">
                <h3 className="font-bold text-light mb-4">Order Details</h3>
                
                <div className="flex items-start mb-6">
                  <div className="w-16 h-16 bg-gray-800 flex items-center justify-center mr-4 border border-gray-700">
                    <span className="text-gray-400 text-xs">Image</span>
                  </div>
                  
                  <div className="flex-1">
                    <h4 className="font-medium text-light mb-1">{order.productName}</h4>
                    {order.selectedSize && (
                      <p className="text-gray-400 text-sm">Size: {order.selectedSize}</p>
                    )}
                  </div>
                  
                  <div className="text-right">
                    <span className="font-medium text-light">{formatIdrFromUsd(order.price)}</span>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Subtotal:</span>
                    <span className="text-light">{formatIdrFromUsd(order.price)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Shipping:</span>
                    <span className="text-light">
                      {formatIdrFromUsd(order.shipping.cost)}
                      {order.shipping.option && ` (${order.shipping.option.carrier} - ${order.shipping.option.service})`}
                    </span>
                  </div>
                  <div className="flex justify-between font-bold pt-2 border-t border-gray-800 mt-2">
                    <span className="text-light">Total:</span>
                    <span className="text-primary">{formatIdrFromUsd(order.total)}</span>
                  </div>
                </div>
              </div>
              
              <div className="border-t border-gray-800 pt-6 mb-6">
                <h3 className="font-bold text-light mb-4">Shipping Information</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium text-light mb-2">Shipping Address</h4>
                    <p className="text-gray-400">{order.shippingAddress.fullName}</p>
                    <p className="text-gray-400">{order.shippingAddress.addressLine1}</p>
                    {order.shippingAddress.addressLine2 && (
                      <p className="text-gray-400">{order.shippingAddress.addressLine2}</p>
                    )}
                    <p className="text-gray-400">
                      {order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.postalCode}
                    </p>
                    <p className="text-gray-400">{order.shippingAddress.country}</p>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-light mb-2">Contact Information</h4>
                    <p className="text-gray-400">{order.shippingAddress.email}</p>
                    <p className="text-gray-400">{order.shippingAddress.phone}</p>
                  </div>
                </div>
              </div>
              
              <div className="border-t border-gray-800 pt-6">
                <h3 className="font-bold text-light mb-4">Shipping Details</h3>
                
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Carrier:</span>
                    <span className="text-light">{order.shipping.option.carrier}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Service:</span>
                    <span className="text-light">{order.shipping.option.service}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Estimated Delivery:</span>
                    <span className="text-light">{order.shipping.option.estimatedDelivery}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-secondary border border-gray-800 rounded-lg overflow-hidden mb-8">
            <div className="p-6">
              <h2 className="font-serif text-xl font-bold text-primary mb-4">
                What's Next?
              </h2>
              
              <div className="space-y-4">
                <div className="flex">
                  <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                    <span className="text-primary font-bold">1</span>
                  </div>
                  <div>
                    <h3 className="font-bold text-light mb-1">Order Confirmation</h3>
                    <p className="text-gray-400">
                      You'll receive an order confirmation email with your order number and details.
                    </p>
                  </div>
                </div>
                
                <div className="flex">
                  <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                    <span className="text-primary font-bold">2</span>
                  </div>
                  <div>
                    <h3 className="font-bold text-light mb-1">Crafting Your Hat</h3>
                    <p className="text-gray-400">
                      Our artisans will begin crafting your hat by hand in our Bali workshop.
                    </p>
                  </div>
                </div>
                
                <div className="flex">
                  <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                    <span className="text-primary font-bold">3</span>
                  </div>
                  <div>
                    <h3 className="font-bold text-light mb-1">Shipping</h3>
                    <p className="text-gray-400">
                      Once your hat is ready, we'll ship it to you and provide tracking information.
                    </p>
                  </div>
                </div>
                
                <div className="flex">
                  <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                    <span className="text-primary font-bold">4</span>
                  </div>
                  <div>
                    <h3 className="font-bold text-light mb-1">Delivery</h3>
                    <p className="text-gray-400">
                      Your hat will be delivered to your shipping address. Enjoy your new Kala hat!
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="text-center">
            <p className="text-gray-400 mb-6">
              If you have any questions about your order, please contact us at <span className="text-primary">support@kalahats.com</span>
            </p>
            
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button 
                variant="secondary" 
                onClick={() => navigate('/products')}
              >
                Continue Shopping
              </Button>
              <Button 
                variant="primary" 
                onClick={() => navigate('/')}
              >
                Return to Home
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default OrderComplete;
